// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — onglet « Campagnes » d'une organisation (campagnes de vente subventionnée, moteur générique) :
// liste paginée (filtre d'état) avec la période d'inscription et les unités inscrites sur la quantité disponible
// (champs de la liste quand le serveur les renvoie — DM-WEB-003 n° 2 —, sinon lus sur le détail de chaque campagne), création d'une campagne en brouillon sur un dispositif de type campagne
// (gabarit « campagne de vente subventionnée » ; le règlement se prépare au Centre de configuration).
// Routes : GET/POST /api/dispositifs/campagnes, GET /api/dispositifs/campagnes/:id.
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Megaphone, Plus } from 'lucide-react';
import { Bouton, BandeauInfo, Squelette } from '../../design/composants';
import { toArray } from './api';
import { Champ, PaginationDs } from './commun';
import { dateLongue } from './apiSocial';
import { useLectureSociale, EtatsLecture, FiltreEtats, FenetreActe } from './communSocial';
import { useProduits } from './PretsSection';
import { useCampagnes, quantiteUnites, ETATS_CAMPAGNE, CYCLE_CAMPAGNE } from './apiCampagnes';
import { EtatCampagne, MarquesCampagne, LienCentre, unitesInscrites } from './communCampagnes';
import { LigneSuivi } from '../../components/suivi/SuiviDemande'; // prochain acteur : `suivi` des lignes (lecture)

/** Cellules « période » et « unités » : `r` = { date_ouverture, date_cloture, quantite_disponible } ou null, `inscrites` = unités. */
function CellulesPeriode({ campagne, r, inscrites }) {
  return (
    <>
      <td style={{ whiteSpace: 'nowrap' }}>
        {r && r.date_ouverture ? <>{dateLongue(r.date_ouverture)}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>au {dateLongue(r.date_cloture)}</span></>
          : <span className="rh-cellule-secondaire">Règlement à compléter</span>}
      </td>
      <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        {quantiteUnites(campagne, inscrites)}
        <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{r && r.quantite_disponible !== null && r.quantite_disponible !== undefined ? `inscrites sur ${r.quantite_disponible} disponibles` : 'inscrites'}</span>
      </td>
    </>
  );
}

/** Repli : cellules lues sur le détail de la campagne (une requête par ligne) quand la liste ne porte pas ces champs. */
function DetailLigne({ campagne }) {
  const api = useCampagnes();
  const [d, setD] = useState(null);
  const [indispo, setIndispo] = useState(false);
  useEffect(() => {
    let vivant = true;
    api.campagne(campagne.id).then((x) => { if (vivant) setD(x); }).catch(() => { if (vivant) setIndispo(true); });
    return () => { vivant = false; };
  }, [api, campagne.id]);
  if (indispo) return <><td><span className="rh-cellule-secondaire">—</span></td><td><span className="rh-cellule-secondaire">—</span></td></>;
  if (!d) return <><td><Squelette largeur="80%" /></td><td><Squelette largeur="60%" /></td></>;
  return <CellulesPeriode campagne={campagne} r={d.reglement} inscrites={unitesInscrites(d.inscriptions)} />;
}

/** La liste porte-t-elle la période et les unités (DM-WEB-003 n° 2) ? Alors aucune requête par ligne. */
const listeEnrichie = (c) => c.unites_inscrites !== undefined && c.date_ouverture !== undefined;

export function FenetreNouvelleCampagne({ organisationId, produits, onFermer, onCree }) {
  const api = useCampagnes();
  const [dispositif, setDispositif] = useState(produits.length === 1 ? produits[0].id : '');
  const [lib, setLib] = useState('');
  const [singulier, setSingulier] = useState('');
  const [pluriel, setPluriel] = useState('');
  const pret = dispositif && lib.trim().length >= 3 && singulier.trim().length >= 2 && pluriel.trim().length >= 2;
  return (
    <FenetreActe titre="Nouvelle campagne" Icone={Megaphone} libelleBouton="Créer en brouillon" pret={!!pret} onFermer={onFermer}
      aide="La campagne est créée en brouillon avec son numéro. Son règlement (prix, aide, quantités, dates, durée de remboursement) se prépare, se simule et se fait valider au Centre de configuration ; une autre personne valide ensuite la campagne."
      onConfirmer={async () => {
        const c = await api.creerCampagne({ dispositif_id: dispositif, libelle: lib.trim(), unite_singulier: singulier.trim(), unite_pluriel: pluriel.trim() });
        onCree(c);
      }}>
      <Champ libelle="Dispositif (gabarit : campagne de vente subventionnée)" requis>
        <select className="rh-champ-saisie" value={dispositif} onChange={(e) => setDispositif(e.target.value)}>
          <option value="">Choisir…</option>
          {produits.map((p) => <option key={p.id} value={p.id} disabled={p.etat === 'ARCHIVE'}>{p.libelle}{p.etat !== 'ACTIF' ? ' (non actif)' : ''}</option>)}
        </select>
      </Champ>
      <Champ libelle="Libellé de la campagne" requis aide="3 à 80 caractères ; c'est le titre vu par les salariés.">
        <input className="rh-champ-saisie" maxLength={80} value={lib} onChange={(e) => setLib(e.target.value)} />
      </Champ>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <Champ libelle="Unité (singulier)" requis aide="Ce qui est vendu, au singulier."><input className="rh-champ-saisie" maxLength={24} value={singulier} onChange={(e) => setSingulier(e.target.value)} /></Champ>
        <Champ libelle="Unité (pluriel)" requis><input className="rh-champ-saisie" maxLength={24} value={pluriel} onChange={(e) => setPluriel(e.target.value)} /></Champ>
      </div>
      <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>
        La campagne naît en simulation : les créances qui en naîtront ne seront jamais transmises à la Paie. Le passage au réel est un acte dédié,
        préparé puis confirmé par deux personnes habilitées, depuis la fiche de la campagne.
      </p>
      <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>Règlement de la campagne : <LienCentre portee={{ organisation: organisationId, dispositif }} /></p>
    </FenetreActe>
  );
}

export default function CampagnesSection({ organisation, droits }) {
  const api = useCampagnes();
  const naviguer = useNavigate();
  const [etat, setEtat] = useState('');
  const [page, setPage] = useState(1);
  const [creation, setCreation] = useState(false);
  const { produits } = useProduits(organisation.id, 'CAMPAGNE');
  const liste = useLectureSociale(() => api.campagnes(organisation.id, { page, taille: 25, etat }), [api, organisation.id, page, etat]);
  const elements = toArray(liste.donnees);
  const ouvrir = (c) => naviguer(`/dispositifs/${organisation.id}/campagnes/${c.id}`);
  const peutPreparer = droits.a('ds_campagne_preparer') && organisation.etat === 'ACTIVE';
  return (
    <>
      <div className="rh-outils" style={{ justifyContent: 'space-between' }}>
        <FiltreEtats table={ETATS_CAMPAGNE} codes={[...CYCLE_CAMPAGNE, 'ANNULEE']} valeur={etat} onChange={(c) => { setEtat(c); setPage(1); }} />
        {peutPreparer && <Bouton variante="primaire" Icone={Plus} disabled={!produits.length} onClick={() => setCreation(true)}>Nouvelle campagne</Bouton>}
      </div>
      {peutPreparer && !produits.length && liste.donnees && (
        <BandeauInfo ton="info">Aucun dispositif de type campagne dans cette organisation : créez-le d'abord dans l'onglet Dispositifs.</BandeauInfo>
      )}
      <section className="rh-carte" style={{ padding: 0 }}>
        <EtatsLecture lecture={liste} Icone={Megaphone} estVide={!elements.length}
          titreVide={etat ? 'Aucune campagne dans cet état' : 'Aucune campagne'}
          vide={etat ? 'Choisissez un autre état.' : 'Une campagne de vente subventionnée se prépare en brouillon, se simule, puis est validée par une autre personne avant d\'ouvrir les inscriptions.'}>
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Campagne</th><th>Inscriptions</th><th>Unités</th><th>État</th><th aria-label="Ouvrir" /></tr></thead>
              <tbody>
                {elements.map((c) => (
                  <tr key={c.id} className="rh-ligne" tabIndex={0} onClick={() => ouvrir(c)} onKeyDown={(e) => { if (e.key === 'Enter') ouvrir(c); }}>
                    <td>
                      <span className="rh-cellule-principale">{c.libelle}</span>
                      <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{c.numero} <MarquesCampagne campagne={c} /></span>
                    </td>
                    {listeEnrichie(c) ? <CellulesPeriode campagne={c} r={c} inscrites={c.unites_inscrites} /> : <DetailLigne campagne={c} />}
                    <td><EtatCampagne etat={c.etat} /><LigneSuivi suivi={c.suivi} /></td>
                    <td style={{ width: 32, color: 'var(--sg-texte-4)' }}><ChevronRight size={16} aria-hidden="true" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationDs donnees={liste.donnees} onPage={setPage} />
        </EtatsLecture>
      </section>
      {creation && <FenetreNouvelleCampagne organisationId={organisation.id} produits={produits} onFermer={() => setCreation(false)} onCree={(c) => { setCreation(false); ouvrir(c); }} />}
    </>
  );
}
